import React, { useState, useEffect } from 'react';
import './MessagesManager.css';
import { getAllMessages, updateMessageStatus, deleteMessage } from '../../firebase/dataService';

function MessagesManager({ onUnreadCountChange }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const data = await getAllMessages();
      setMessages(data);
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMessageClick = async (message) => {
    setSelectedMessage(message);
    setShowModal(true);
    
    if (!message.read) {
      try {
        await updateMessageStatus(message.id, 'read');
        setMessages(messages.map(msg => 
          msg.id === message.id ? { ...msg, read: true, status: 'read' } : msg
        ));
        
        // تحديث عداد الرسائل غير المقروءة في Dashboard
        if (onUnreadCountChange) {
          const newUnreadCount = messages.filter(msg => 
            msg.id === message.id ? false : !msg.read
          ).length;
          onUnreadCountChange(newUnreadCount);
        }
      } catch (error) {
        console.error('Error marking message as read:', error);
      }
    }
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه الرسالة؟')) {
      return;
    }

    try {
      await deleteMessage(messageId);
      setMessages(messages.filter(msg => msg.id !== messageId));
      if (selectedMessage?.id === messageId) {
        setSelectedMessage(null);
        setShowModal(false);
      }
    } catch (error) {
      console.error('Error deleting message:', error);
      alert('فشل حذف الرسالة');
    }
  };

  const handleToggleRead = async (message) => {
    const newStatus = message.read ? 'unread' : 'read';
    
    try {
      await updateMessageStatus(message.id, newStatus);
      setMessages(messages.map(msg => 
        msg.id === message.id ? { ...msg, read: !msg.read, status: newStatus } : msg
      ));
      
      // تحديث عداد الرسائل غير المقروءة في Dashboard
      if (onUnreadCountChange) {
        const newUnreadCount = messages.filter(msg => 
          msg.id === message.id ? (newStatus === 'unread') : !msg.read
        ).length;
        onUnreadCountChange(newUnreadCount);
      }
    } catch (error) {
      console.error('Error toggling message status:', error);
    }
  };

  const getFilteredMessages = () => {
    if (filter === 'unread') {
      return messages.filter(msg => !msg.read);
    } else if (filter === 'read') {
      return messages.filter(msg => msg.read);
    }
    return messages;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (hours < 1) {
      return 'منذ قليل';
    } else if (hours < 24) {
      return `منذ ${hours} ساعة`;
    } else if (days < 7) {
      return `منذ ${days} يوم`;
    } else {
      return date.toLocaleDateString('ar-SA');
    }
  };

  const filteredMessages = getFilteredMessages();
  const unreadCount = messages.filter(msg => !msg.read).length;

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner-large"></div>
        <p>جاري تحميل الرسائل...</p>
      </div>
    );
  }

  return (
    <div className="messages-manager">
      <div className="messages-header">
        <div className="header-stats">
          <div className="stat-item">
            <span className="stat-label">إجمالي الرسائل:</span>
            <span className="stat-value">{messages.length}</span>
          </div>
          <div className="stat-item unread">
            <span className="stat-label">غير مقروءة:</span>
            <span className="stat-value">{unreadCount}</span>
          </div>
        </div>

        <div className="filter-buttons">
          <button 
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            الكل ({messages.length})
          </button>
          <button 
            className={`filter-btn ${filter === 'unread' ? 'active' : ''}`}
            onClick={() => setFilter('unread')}
          >
            غير مقروءة ({unreadCount})
          </button>
          <button 
            className={`filter-btn ${filter === 'read' ? 'active' : ''}`}
            onClick={() => setFilter('read')}
          >
            مقروءة ({messages.length - unreadCount})
          </button>
        </div>
      </div>

      <div className="messages-content">
        <div className="messages-list">
          {filteredMessages.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📭</div>
              <p>لا توجد رسائل</p>
            </div>
          ) : (
            filteredMessages.map(message => (
              <div 
                key={message.id}
                className={`message-item ${!message.read ? 'unread' : ''} ${selectedMessage?.id === message.id ? 'selected' : ''}`}
                onClick={() => handleMessageClick(message)}
              >
                <div className="message-item-header">
                  <div className="sender-info">
                    <span className="sender-name">{message.name}</span>
                    {!message.read && <span className="unread-badge">جديد</span>}
                  </div>
                  <span className="message-date">{formatDate(message.createdAt)}</span>
                </div>
                <div className="message-item-subject">
                  {message.subject || 'بدون موضوع'}
                </div>
                <div className="message-item-preview">
                  {message.message.substring(0, 80)}...
                </div>
              </div>
            ))
          )}
        </div>

        <div className="message-details">
          {selectedMessage ? (
            <>
              <div className="details-header">
                <h3 className="details-title">{selectedMessage.subject || 'بدون موضوع'}</h3>
                <div className="details-actions">
                  <button 
                    className="action-btn"
                    onClick={() => handleToggleRead(selectedMessage)}
                    title={selectedMessage.read ? 'تحديد كغير مقروءة' : 'تحديد كمقروءة'}
                  >
                    {selectedMessage.read ? '📧' : '✉️'}
                  </button>
                  <button 
                    className="action-btn delete"
                    onClick={() => handleDelete(selectedMessage.id)}
                    title="حذف"
                  >
                    🗑️
                  </button>
                </div>
              </div>

              <div className="details-sender">
                <div className="sender-detail">
                  <span className="detail-label">من:</span>
                  <span className="detail-value">{selectedMessage.name}</span>
                </div>
                <div className="sender-detail">
                  <span className="detail-label">البريد:</span>
                  <a href={`mailto:${selectedMessage.email}`} className="detail-value email">
                    {selectedMessage.email}
                  </a>
                </div>
                <div className="sender-detail">
                  <span className="detail-label">التاريخ:</span>
                  <span className="detail-value">
                    {new Date(selectedMessage.createdAt).toLocaleString('ar-SA')}
                  </span>
                </div>
              </div>

              <div className="details-message">
                <h4 className="message-label">الرسالة:</h4>
                <p className="message-text">{selectedMessage.message}</p>
              </div>
            </>
          ) : (
            <div className="no-selection">
              <div className="no-selection-icon">📬</div>
              <p>اختر رسالة لعرض التفاصيل</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal for mobile */}
      {showModal && selectedMessage && (
        <div className="message-modal-overlay" onClick={closeModal}>
          <div className="message-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedMessage.subject || 'بدون موضوع'}</h3>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="modal-message">
                <h4>الرسالة:</h4>
                <p>{selectedMessage.message}</p>
              </div>

              <div className="modal-sender-info">
                <div className="modal-info-item">
                  <span className="modal-label">من:</span>
                  <span className="modal-value">{selectedMessage.name}</span>
                </div>
                <div className="modal-info-item">
                  <span className="modal-label">البريد:</span>
                  <a href={`mailto:${selectedMessage.email}`} className="modal-value modal-email">
                    {selectedMessage.email}
                  </a>
                </div>
                <div className="modal-info-item">
                  <span className="modal-label">التاريخ:</span>
                  <span className="modal-value">
                    {new Date(selectedMessage.createdAt).toLocaleString('ar-SA')}
                  </span>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="modal-action-btn"
                onClick={() => {
                  handleToggleRead(selectedMessage);
                  closeModal();
                }}
              >
                {selectedMessage.read ? '📧 تعليم كـ غير مقروء' : '✉️ تعليم كـ مقروء'}
              </button>
              <button 
                className="modal-action-btn modal-delete-btn"
                onClick={() => {
                  handleDelete(selectedMessage.id);
                  closeModal();
                }}
              >
                🗑️ حذف
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MessagesManager;
